import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { Brain } from 'lucide-react';
export default function Neurodevelopmental() {
  return <main className="min-h-screen bg-white" data-id="element-1261">
      <Helmet data-id="element-1262">
        <title data-id="element-1263">ADHD & Autism Medico-Legal Assessments | Expert Reports</title>
        <meta name="description" content="Comprehensive ADHD and autism spectrum assessments for court proceedings. Expert medico-legal reports on neurodevelopmental conditions by Dr Imtiaz Syed." data-id="element-1264" />
        <meta name="keywords" content="ADHD & Autism Medico-Legal Assessments, ADHD Assessment, Autism Evaluation, Neurodevelopmental Disorders, Expert Witness, Legal Capacity, ADHD medico-legal reports for court cases UK" data-id="element-1265" />
        <script type="application/ld+json" data-id="element-1266">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "ADHD & Autism Medico-Legal Assessments",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Neurodevelopmental Assessment",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert ADHD & Autism Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1267">
        <div className="container mx-auto px-4" data-id="element-1268">
          <div className="flex items-center justify-center mb-4" data-id="element-1269">
            <Brain className="h-12 w-12 mr-4" data-id="element-1270" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-1271">
              ADHD & Autism Medico-Legal Assessments
            </h1>
          </div>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1272">
            Specialized evaluations of ADHD, Autism, and their legal
            implications
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1273">
        <div className="container mx-auto px-4" data-id="element-1274">
          <div className="max-w-3xl mx-auto" data-id="element-1275">
            <div className="prose max-w-none" data-id="element-1276">
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1277">
                Expert Neurodevelopmental Evaluations
              </h2>
              <p className="text-slate-dark mb-6" data-id="element-1278">
                Dr. Syed provides comprehensive assessments of
                neurodevelopmental conditions, with a particular focus on
                Attention-Deficit/Hyperactivity Disorder (ADHD) and Autism
                Spectrum Disorder (ASD). These specialized evaluations are
                crucial for understanding how these conditions may impact an
                individual's legal context, decision-making capacity, or
                behavior.
              </p>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1279">
                Our ADHD & Autism Assessment Services Include:
              </h3>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1280">
                <li className="mb-2" data-id="element-1281">
                  <strong data-id="element-1282">ADHD Evaluations:</strong> Comprehensive assessments
                  of attention, impulsivity, and hyperactivity symptoms, with
                  consideration of how these may affect legal capacity,
                  culpability, or compliance with legal requirements.
                </li>
                <li className="mb-2" data-id="element-1283">
                  <strong data-id="element-1284">Autism Spectrum Assessments:</strong> Detailed
                  evaluations of social communication, interaction patterns, and
                  restricted/repetitive behaviors, with analysis of how these
                  features may influence legal understanding and behavior.
                </li>
                <li className="mb-2" data-id="element-1285">
                  <strong data-id="element-1286">Impact on Decision-Making Capacity:</strong> Specific
                  assessments of how neurodevelopmental conditions may affect an
                  individual's capacity to make decisions in various legal
                  contexts.
                </li>
                <li className="mb-2" data-id="element-1287">
                  <strong data-id="element-1288">Behavioral Analysis:</strong> Expert interpretation of
                  how neurodevelopmental features may have influenced past
                  behaviors relevant to legal proceedings.
                </li>
                <li data-id="element-1289">
                  <strong data-id="element-1290">Reasonable Adjustments:</strong> Recommendations for
                  appropriate accommodations in legal settings to ensure fair
                  treatment of individuals with neurodevelopmental conditions.
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1291">
                Our Methodology
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1292">
                Dr. Syed's neurodevelopmental assessments are characterized by:
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1293">
                <li className="mb-2" data-id="element-1294">
                  <strong data-id="element-1295">Comprehensive Approach:</strong> Detailed clinical
                  interviews, standardized assessments, and consideration of
                  developmental history.
                </li>
                <li className="mb-2" data-id="element-1296">
                  <strong data-id="element-1297">Contextual Understanding:</strong> Careful analysis of
                  how neurodevelopmental features manifest in specific
                  situations relevant to the legal context.
                </li>
                <li className="mb-2" data-id="element-1298">
                  <strong data-id="element-1299">Differential Diagnosis:</strong> Consideration of
                  other conditions that may mimic or co-occur with ADHD or ASD.
                </li>
                <li data-id="element-1300">
                  <strong data-id="element-1301">Legal Relevance:</strong> Clear articulation of how
                  identified neurodevelopmental features relate to specific
                  legal questions.
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1302">
                When to Request an ADHD or Autism Assessment
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1303">
                These evaluations are particularly valuable in:
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1304">
                <li className="mb-2" data-id="element-1305">
                  Criminal proceedings where ADHD or autism may have influenced
                  behavior
                </li>
                <li className="mb-2" data-id="element-1306">
                  Capacity assessments where neurodevelopmental conditions may
                  affect decision-making
                </li>
                <li className="mb-2" data-id="element-1307">
                  Family court proceedings involving parents or children with
                  neurodevelopmental conditions
                </li>
                <li className="mb-2" data-id="element-1308">
                  Employment tribunal cases related to disability discrimination
                </li>
                <li data-id="element-1309">
                  Immigration cases where vulnerability due to
                  neurodevelopmental conditions is relevant
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1310">
                Expert Court Testimony
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1311">
                Dr. Syed provides clear, accessible expert testimony on
                neurodevelopmental conditions, helping courts and tribunals
                understand the complex ways these conditions may influence
                behavior, capacity, and legal responsibility.
              </p>

              {/* Related Services */}
              <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-1312">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1313">
                  Explore Our Other Services
                </h3>
                <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-1314">
                  <Link to="/services/mental-capacity" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1315">
                    Mental Capacity Assessments
                  </Link>
                  <Link to="/services/forensic-risk" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1316">
                    Forensic & Risk Assessments
                  </Link>
                  <Link to="/services/specialist" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1317">
                    Psychiatric Reports for Courts & Tribunals
                  </Link>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 shadow-md mb-8" data-id="element-1318">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1319">
                  Ready to Instruct Dr. Syed?
                </h3>
                <p className="text-slate-dark mb-4" data-id="element-1320">
                  For neurodevelopmental assessments that provide crucial
                  insights for legal proceedings, contact us today to discuss
                  your case requirements.
                </p>
                <Button variant="primary" href="/contact" data-id="element-1321">
                  Book an ADHD or Autism Assessment
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}